/**
 * Utilidades para formatear datos
 */

import { labelCajaTipo } from '../constants';

/**
 * Formatea un número como precio con separadores de miles
 * @param {number|string|null|undefined} precio - Precio a formatear
 * @param {string} fallback - Valor a mostrar si el precio es inválido (default: '-')
 * @returns {string} Precio formateado
 */
export const formatPrecio = (precio, fallback = '-') => {
  if (precio == null || precio === '') return fallback;
  const num = Number(precio);
  return Number.isFinite(num) ? `$${num.toLocaleString()}` : fallback;
};

/**
 * Convierte un valor a número seguro
 * @param {any} val - Valor a convertir
 * @param {number} defaultValue - Valor por defecto si la conversión falla (default: 0)
 * @returns {number} Número válido
 */
export const toNumber = (val, defaultValue = 0) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : defaultValue;
};

/**
 * Capitaliza la primera letra de cada palabra
 * @param {string} texto - Texto a capitalizar
 * @returns {string} Texto capitalizado
 */
export const capitalizarPalabras = (texto) => {
  if (!texto || typeof texto !== 'string') return '';
  return texto
    .trim()
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Parsea medidas desde un texto tipo "30x20x15" o "30 x 20 x 15 cm".
 * Orden: ancho × alto × profundidad.
 * @param {string|null|undefined} modelo
 * @returns {{ ancho: number, alto: number, profundidad: number }|null}
 */
export const parseMedidasFromModelo = (modelo) => {
  if (!modelo || typeof modelo !== 'string') return null;
  const m = modelo.match(
    /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i
  );
  if (!m) return null;
  const ancho = Number(String(m[1]).replace(',', '.'));
  const alto = Number(String(m[2]).replace(',', '.'));
  const profundidad = Number(String(m[3]).replace(',', '.'));
  if (![ancho, alto, profundidad].every((n) => Number.isFinite(n) && n > 0)) {
    return null;
  }
  return { ancho, alto, profundidad };
};

/**
 * Formatea medidas de caja para título / listados.
 * @param {{ ancho?: number|null, alto?: number|null, profundidad?: number|null }} dims
 * @returns {string}
 */
export const formatMedidaCaja = ({ ancho, alto, profundidad } = {}) => {
  const a = Number(ancho);
  const h = Number(alto);
  const p = Number(profundidad);
  if (![a, h, p].every((n) => Number.isFinite(n) && n > 0)) return '';
  const fmt = (n) => (Number.isInteger(n) ? String(n) : String(n));
  return `${fmt(a)} x ${fmt(h)} x ${fmt(p)} cm`;
};

/**
 * Obtiene dimensiones de un producto (campos nuevos o parseo de modelo legacy).
 * @param {Object} producto
 * @returns {{ ancho: number, alto: number, profundidad: number }|null}
 */
export const obtenerMedidasProducto = (producto) => {
  if (!producto) return null;
  const ancho = Number(producto.ancho);
  const alto = Number(producto.alto);
  const profundidad = Number(producto.profundidad);
  if ([ancho, alto, profundidad].every((n) => Number.isFinite(n) && n > 0)) {
    return { ancho, alto, profundidad };
  }
  return parseMedidasFromModelo(producto.modelo);
};

/**
 * Título de producto: categoría + medida (cajas).
 * Otros productos: medida / material / color.
 * @param {Object} producto
 * @returns {string}
 */
export const generarTituloProducto = (producto) => {
  if (!producto) return 'Producto sin título';

  const esCaja = producto.categoria === 'fundas' || !producto.categoria;
  const medida = (producto.modelo || '').trim();
  const categoriaLabel = labelCajaTipo(producto.material);

  if (esCaja) {
    const partes = [];
    if (categoriaLabel) partes.push(categoriaLabel);
    if (medida) partes.push(medida);
    if (partes.length > 0) return partes.join(' ');
    return capitalizarPalabras(producto.nombre || 'Producto sin título');
  }

  const partes = [];
  if (medida) partes.push(capitalizarPalabras(medida));
  if (producto.material?.trim()) {
    partes.push(capitalizarPalabras(producto.material.trim()));
  }
  if (producto.color?.trim()) {
    partes.push(capitalizarPalabras(producto.color.trim()));
  }
  if (partes.length === 0) {
    return capitalizarPalabras(producto.nombre || 'Producto sin título');
  }
  return partes.join(' ');
};

/**
 * Normaliza un string para búsquedas (lowercase y trim)
 * @param {string} str - String a normalizar
 * @returns {string} String normalizado
 */
export const normalizeString = (str) => {
  return (str || '').toString().toLowerCase().trim();
};
